import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import Amazon100 from 'assets/images/offers/amazon100.png';
import Amazon50 from 'assets/images/offers/amazon50.png';
import Amazon75 from 'assets/images/offers/amazon75.png';
import Aritzia100 from 'assets/images/offers/aritzia100.png';
import Aritzia50 from 'assets/images/offers/aritzia50.png';
import Aritzia75 from 'assets/images/offers/aritzia75.png';
import Mac50 from 'assets/images/offers/mac50.png';
import Mac75 from 'assets/images/offers/mac75.png';
import Mastercard from 'assets/images/offers/mastercard.png';
import Mastercard100 from 'assets/images/offers/mastercard100.png';
import Mastercard25 from 'assets/images/offers/mastercard25.png';
import Mastercard50 from 'assets/images/offers/mastercard50.png';
import Mastercard60 from 'assets/images/offers/mastercard60.png';
import Mastercard75 from 'assets/images/offers/mastercard75.png';
import Visa from 'assets/images/offers/visa.png';
import Visa100 from 'assets/images/offers/visa100.png';
import Visa25 from 'assets/images/offers/visa25.png';
import Visa50 from 'assets/images/offers/visa50.png';
import Visa60 from 'assets/images/offers/visa60.png';
import Visa75 from 'assets/images/offers/visa75.png';
import Zara100 from 'assets/images/offers/zara100.png';
import Zara50 from 'assets/images/offers/zara50.png';
import Zara75 from 'assets/images/offers/zara75.png';
import { COUNTRY } from 'common/constants';
import { RequestOffer } from 'connect/saga/handlers/offer/request-offer';
import { RequestReferralPromotion } from 'connect/saga/handlers/offer/request-referral-promotion';
import { useHookResult } from 'pages/funnel/storage/hooks';
import { useFunnelStorage } from 'pages/funnel/storage/Storage';
import { STEP_KEY } from 'pages/funnel/utils';

export const OFFER_TYPES = {
  NONE: 'none',
  REFERRAL_PROMOTION: 'referral',
  PROMO_CODE: 'promo-code',
};

export const OFFER_IMAGES = {
  amazon100: Amazon100,
  amazon50: Amazon50,
  amazon75: Amazon75,
  aritzia100: Aritzia100,
  aritzia50: Aritzia50,
  aritzia75: Aritzia75,
  mac50: Mac50,
  mac75: Mac75,
  mastercard: Mastercard,
  mastercard25: Mastercard25,
  mastercard50: Mastercard50,
  mastercard60: Mastercard60,
  mastercard75: Mastercard75,
  mastercard100: Mastercard100,
  visa100: Visa100,
  visa25: Visa25,
  visa50: Visa50,
  visa60: Visa60,
  visa75: Visa75,
  zara100: Zara100,
  zara50: Zara50,
  zara75: Zara75,
};

export const DEFAULT_OFFER_IMAGES = {
  [COUNTRY.unitedStates]: {
    default: Visa,
    default50: Visa50,
    default60: Visa60,
    default75: Visa75,
    default100: Visa100,
  },
  [COUNTRY.canada]: {
    default: Mastercard,
    default50: Mastercard50,
    default60: Mastercard60,
    default75: Mastercard75,
    default100: Mastercard100,
  },
};

class OfferService {
  constructor({ offerState, referralPromotionState, country }) {
    this._offerState = offerState;
    this._referralPromotionState = referralPromotionState;
    this._country = country;
  }

  get isLoading() {
    return Boolean(
      this._offerState?.loading || this._referralPromotionState?.loading,
    );
  }

  get error() {
    return this._offerState?.error || this._referralPromotionState?.error;
  }

  get hasActiveOffer() {
    return this.offerType !== OFFER_TYPES.NONE;
  }

  get offerType() {
    const offerTypeMap = {
      [OFFER_TYPES.REFERRAL_PROMOTION]: () =>
        this._referralPromotionState?.value,
      [OFFER_TYPES.PROMO_CODE]: () => this._offerState?.value?.active,
      [OFFER_TYPES.NONE]: () => true,
    };

    return Object.keys(offerTypeMap).find(key => {
      const isApplicable = offerTypeMap[key];
      return isApplicable();
    });
  }

  get offerName() {
    const offerNameMap = {
      [OFFER_TYPES.REFERRAL_PROMOTION]: () =>
        this._referralPromotionState?.value?.displayName,
      [OFFER_TYPES.PROMO_CODE]: () => this._offerState?.value?.displayName,
      [OFFER_TYPES.NONE]: () => null,
    };

    const getName = offerNameMap[this.offerType];
    return getName() || 'Unknown';
  }

  get offerValue() {
    const offerValueMap = {
      [OFFER_TYPES.REFERRAL_PROMOTION]: () => {
        const promotion = this._referralPromotionState?.value;
        return promotion?.referralValue || promotion?.referrerValue;
      },
      [OFFER_TYPES.PROMO_CODE]: () => this._offerState?.value?.cost,
      [OFFER_TYPES.NONE]: () => 0,
    };

    const getValue = offerValueMap[this.offerType];
    return getValue();
  }

  get isPromoCode() {
    return this.offerType === OFFER_TYPES.PROMO_CODE;
  }

  get isReferralPromotion() {
    return this.offerType === OFFER_TYPES.REFERRAL_PROMOTION;
  }

  get isThirdPartyOffer() {
    return this.isPromoCode && this.offerValue === 0;
  }

  get offerImage() {
    if (!this.hasActiveOffer) {
      return null;
    }

    if (this.isThirdPartyOffer) {
      return this._offerState.value.images.square;
    }

    const getImageKey = {
      [OFFER_TYPES.REFERRAL_PROMOTION]: () => {
        const {
          giftCardType,
          referralValue,
        } = this._referralPromotionState.value;
        return `${giftCardType.toLowerCase()}${referralValue}`;
      },
      [OFFER_TYPES.PROMO_CODE]: () => this._offerState.value.slug,
      [OFFER_TYPES.NONE]: () => null,
    }[this.offerType];

    const imageKey = getImageKey();
    if (OFFER_IMAGES[imageKey]) {
      return OFFER_IMAGES[imageKey];
    }

    const country = Object.keys(DEFAULT_OFFER_IMAGES).includes(this._country)
      ? this._country
      : COUNTRY.unitedStates;

    const value = this.offerValue;
    if (DEFAULT_OFFER_IMAGES[country][`default${value}`]) {
      return DEFAULT_OFFER_IMAGES[country][`default${value}`];
    }

    return DEFAULT_OFFER_IMAGES[country].default;
  }
}

export const useOfferService = () => {
  const funnelStorage = useFunnelStorage();

  const referralPromotionState = useSelector(RequestReferralPromotion.selector);
  const offerState = useSelector(RequestOffer.selector);
  const country = funnelStorage?.get(STEP_KEY.LOCATION)?.address?.country;

  const createServiceParams = useCallback(() => ({
    offerState,
    referralPromotionState,
    country,
  }), [offerState, referralPromotionState, country]);

  const createOfferService = useCallback(() => new OfferService(createServiceParams()), [createServiceParams]);

  return useHookResult(createOfferService());
};
